import { chain } from "lodash-es";
import { useMemo } from "react";

import { getTypedPath } from "@/common/helpers/typedPath";
import { CustomerDto, CustomerExternalUserLinkDto } from "@/core/api/generated/v1";

import DataTabular from "../../DataTabular/DataTabular";
import Datetime from "../../Datetime/Datetime";
import GeneralByWhoDisplay from "../General/Display/GeneralByWhoDisplay";
import IntegrationApiClientLink from "../IntegrationApiClient/IntegrationApiClientLink";
import CustomerContactCard from "./CustomerContactCard";
import CustomerContactChip from "./CustomerContactChip";

interface Props {
  customer: CustomerDto;
  links: CustomerExternalUserLinkDto[] | null | undefined;
}

export default function CustomerExternalUserLinkList({ customer, links }: Props) {
  const contactsByIdMap = useMemo(
    () =>
      chain(customer.contacts || [])
        .keyBy((x) => x.id || "")
        .mapValues((x) => x)
        .value(),
    [customer.contacts],
  );

  const linksComputed = useMemo(
    () =>
      chain(links || [])
        .orderBy((x) => x.unlinkedAt || x.linkedAt, "desc")
        .value(),
    [links],
  );

  if (!linksComputed) {
    return null;
  }

  return (
    <DataTabular<CustomerExternalUserLinkDto>
      columns={[
        {
          field: getTypedPath<CustomerExternalUserLinkDto>().id.$path,
          title: "Link ID",
          width: 250,
          isVisible: false,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) => item.id,
        },
        {
          field: getTypedPath<CustomerExternalUserLinkDto>().externalUserId.$path,
          title: "External user ID",
          width: 250,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) => item.externalUserId,
        },
        {
          field: getTypedPath<CustomerExternalUserLinkDto>().contactId.$path,
          title: "Contact",
          width: 350,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            item.contactId && contactsByIdMap[item.contactId] ? (
              <CustomerContactChip
                contact={contactsByIdMap[item.contactId]}
                popoverContent={
                  <CustomerContactCard
                    contact={contactsByIdMap[item.contactId]}
                    sx={{ minWidth: 300 }}
                  />
                }
                // sx={{ minWidth: 300 }}
              />
            ) : (
              "-"
            ),
        },
        {
          field: getTypedPath<CustomerExternalUserLinkDto>().userId.$path,
          title: "Contact user",
          width: 250,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            item.userId ? (
              <GeneralByWhoDisplay
                who={{ userId: item.userId }}
                size='compact'
                displayProps={{ detailsPopover: false }}
              />
            ) : (
              "-"
            ),
        },
        {
          field: getTypedPath<CustomerExternalUserLinkDto>().integrationApiClientId.$path,
          title: "Integration API client",
          width: 250,
          isVisible: false,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            item.integrationApiClientId ? (
              <IntegrationApiClientLink entityId={item.integrationApiClientId} entity={undefined} />
            ) : (
              "-"
            ),
        },
        {
          field: getTypedPath<CustomerExternalUserLinkDto>().linkedAt.$path,
          title: "Linked at",
          width: 150,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) => (item.linkedAt ? <Datetime datetime={item.linkedAt} /> : "-"),
        },
        {
          field: getTypedPath<CustomerExternalUserLinkDto>().unlinkedAt.$path,
          title: "Unlinked at",
          width: 150,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) => (item.unlinkedAt ? <Datetime datetime={item.unlinkedAt} /> : "-"),
        },
      ]}
      rows={linksComputed}
      getRowId={(item) => item.id!}
    />
  );
}
